/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useEffect, useMemo } from 'react';

import { FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { ConfluenceIcon, JiraIcon, type LogoProps, LoomIcon, RovoIcon } from '@atlaskit/logo';
import { CloseButton } from '@atlaskit/modal-dialog';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { Edition, ProductId } from '../../../../services/modal-data/types';
import ModalBackgroundDark from '../../assets/confirmationBackgroundV2.dark.png';
import ModalBackgroundLight from '../../assets/confirmationBackgroundV2.light.png';
import VisualDark from '../../assets/confirmationVisual.dark.svg';
import VisualLight from '../../assets/confirmationVisual.light.svg';
import TeamworkCollectionLogoDark from '../../assets/teamworkCollectionHeaderV2.dark.png';
import TeamworkCollectionLogoLight from '../../assets/teamworkCollectionHeaderV2.light.png';
import { LegalText } from '../../components/legal-text';
import { ScreenLayout } from '../../components/screen-layout';
import { useModalContext } from '../../contexts/modal-context';
import { useIsDarkMode } from '../../hooks/useIsDarkMode';

import { messages as confirmationMessages } from './messages';
import { getHeadingText } from './utils';

const styles = cssMap({
	content: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		paddingTop: token('space.300'),
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		'@media (min-width: 64rem)': {
			paddingTop: token('space.600'),
			paddingLeft: token('space.600'),
			paddingRight: token('space.600'),
		},
	},
	cta: {
		marginTop: token('space.250'),
		flexGrow: 1,
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-end',
		alignItems: 'end',
	},
	closeButton: {
		position: 'absolute',
		top: token('space.500'),
		right: token('space.500'),
		'@media (min-width: 64rem)': {
			top: token('space.200'),
			right: token('space.200'),
		},
	},
	modalContent: {
		display: 'flex',
		flexGrow: 1,
		flexDirection: 'row',
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.large'),
	},
	leftColumn: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		paddingTop: token('space.500'),
		paddingLeft: token('space.500'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.500'),
	},
	rightColumn: {
		display: 'none',
		'@media (min-width: 64rem)': {
			flexShrink: 0,
			display: 'flex',
			paddingTop: token('space.400'),
			paddingLeft: token('space.400'),
			paddingRight: token('space.400'),
			paddingBottom: token('space.400'),
		},
	},
	heading: {
		marginTop: token('space.300'),
	},
	headingEmphasis: {
		font: token('font.heading.medium'),
		display: 'inline',
		color: token('color.text.brand'),
		whiteSpace: 'nowrap',
	},
	subheading: {
		marginTop: token('space.050'),
		font: token('font.body.small'),
	},
	newApps: {
		marginTop: token('space.300'),
	},
	newApp: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		marginTop: token('space.100'),
	},
	newAppFirst: {
		marginTop: 0,
	},
	newAppText: {
		marginLeft: token('space.150'),
		display: 'flex',
		flexDirection: 'column',
	},
	existingAppsHeader: {
		marginTop: token('space.300'),
		font: token('font.body.small'),
	},
	existingAppsHeaderCompact: {
		marginTop: token('space.200'),
		font: token('font.body.small'),
	},
	existingApps: {
		display: 'flex',
		flexDirection: 'row',
		gap: token('space.200'),
	},
	existingAppText: {
		marginLeft: token('space.075'),
	},
	legalText: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '56px',
		textAlign: 'center',
	},
});

type ConfirmationScreenProps = {
	canActivate: boolean;
	confirmationCtaText: MessageDescriptor;
	confirmationSubheader: MessageDescriptor;
	edition: Edition;
	hasPaymentDetails: boolean;
	onClose: () => void;
	onTryNowClick: () => void;
};

const productIdMapping: Record<
	ProductId,
	{
		descriptionPremium: MessageDescriptor | null;
		descriptionStandard: MessageDescriptor | null;
		header: string;
		Icon: React.ComponentType<LogoProps & { shouldUseHexLogo: boolean }>;
	}
> = {
	confluence: {
		Icon: ConfluenceIcon,
		header: 'Confluence',
		descriptionStandard: confirmationMessages.descriptionConfluence,
		descriptionPremium: null,
	},
	jira: {
		Icon: JiraIcon,
		header: 'Jira',
		descriptionStandard: null,
		descriptionPremium: null,
	},
	rovo: {
		Icon: RovoIcon,
		header: 'Rovo',
		descriptionStandard: confirmationMessages.descriptionRovo,
		descriptionPremium: null,
	},
	loom: {
		Icon: LoomIcon,
		header: 'Loom',
		descriptionStandard: confirmationMessages.descriptionLoomStandard,
		descriptionPremium: confirmationMessages.descriptionLoomPremium,
	},
};

export const ConfirmationScreen: (props: ConfirmationScreenProps) => JSX.Element = ({
	onTryNowClick,
	onClose,
	canActivate,
	confirmationSubheader,
	confirmationCtaText,
}: ConfirmationScreenProps): JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const isDarkMode = useIsDarkMode();
	const { modalState, sourceComponent, sourceContext } = useModalContext();
	const { userProducts, hasPaymentDetails, edition, priceSegmentation, priceValue } = modalState;

	const handleTryNowClick = () => {
		onTryNowClick();

		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcXflowModalConfirmationScreenTryNow',
			attributes: {
				canActivate,
				edition,
				hasPaymentDetails,
				userProducts,
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');
	};

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcXflowModalConfirmationScreen',
			attributes: {
				canActivate,
				edition,
				hasPaymentDetails,
				userProducts,
				priceSegmentation,
				priceValue,
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');
	}, [
		createAnalyticsEvent,
		canActivate,
		edition,
		hasPaymentDetails,
		userProducts,
		priceSegmentation,
		priceValue,
		sourceComponent,
		sourceContext,
	]);

	const newApps = useMemo(() => {
		const eligibleApps: ProductId[] = ['jira', 'confluence', 'loom', 'rovo'];
		return eligibleApps.filter((product) => !userProducts.includes(product));
	}, [userProducts]);

	const existingAppsToShow = useMemo(() => {
		if (!canActivate) {
			const eligibleApps: ProductId[] = ['jira', 'confluence', 'loom', 'rovo'];
			return userProducts.filter((product) => eligibleApps.includes(product));
		}
		return userProducts;
	}, [userProducts, canActivate]);

	const heading = useMemo(
		() => getHeadingText(priceSegmentation, userProducts),
		[priceSegmentation, userProducts],
	);

	return (
		<ScreenLayout
			testId="confirmation-screen-v2"
			ModalBackgroundDark={ModalBackgroundDark}
			ModalBackgroundLight={ModalBackgroundLight}
		>
			<Box xcss={styles.content}>
				<Box xcss={styles.closeButton}>
					<CloseButton onClick={onClose} label={formatMessage(confirmationMessages.closeModal)} />
				</Box>
				<Box xcss={styles.modalContent}>
					<Box xcss={styles.leftColumn}>
						<Image
							src={isDarkMode ? TeamworkCollectionLogoDark : TeamworkCollectionLogoLight}
							srcDark={TeamworkCollectionLogoDark}
							alt={formatMessage(confirmationMessages.teamworkCollectionAlt)}
							testId="teamwork-collection-header-v2"
							width="134"
							height="32"
						/>

						<Box xcss={styles.heading}>
							<Heading size="medium" testId="modal-heading-v2">
								<FormattedMessage
									{...heading.message}
									values={{
										break: <FormattedMessage {...heading.headingBreak} />,
										headingEmphasis: (
											<Inline xcss={styles.headingEmphasis}>
												<FormattedMessage
													{...(heading.noAddedCost
														? confirmationMessages.headingNoPriceIncreaseEmphasis
														: confirmationMessages.headingPriceIncreaseEmphasis)}
												/>
											</Inline>
										),
									}}
								/>
							</Heading>
						</Box>

						<Box xcss={styles.subheading}>
							<Text size="small" color="color.text.subtle" testId="modal-subheading-v2">
								<FormattedMessage {...confirmationSubheader} />
							</Text>
						</Box>

						<Box xcss={styles.newApps}>
							{newApps.map((newApp, index) => {
								const Icon = productIdMapping[newApp].Icon;
								const description =
									(edition === 'premium' && productIdMapping[newApp].descriptionPremium) ||
									productIdMapping[newApp].descriptionStandard;

								return (
									<Box key={newApp} xcss={cx(styles.newApp, index === 0 && styles.newAppFirst)}>
										<Icon
											shouldUseHexLogo
											appearance="brand"
											shouldUseNewLogoDesign
											size="medium"
										/>
										<Box xcss={styles.newAppText}>
											<Heading size="xsmall">{productIdMapping[newApp].header}</Heading>
											{description && (
												<Text size="small">
													<FormattedMessage {...description} />
												</Text>
											)}
										</Box>
									</Box>
								);
							})}
						</Box>
						{existingAppsToShow.length > 0 ? (
							<React.Fragment>
								<Box
									xcss={
										newApps.length > 2
											? styles.existingAppsHeaderCompact
											: styles.existingAppsHeader
									}
								>
									<Text size="small" color="color.text.subtle">
										<FormattedMessage
											{...confirmationMessages.existingAppsHeader}
											values={{ appCount: existingAppsToShow.length }}
										/>
									</Text>
								</Box>

								<Box xcss={styles.existingApps}>
									{existingAppsToShow.map((existingApp) => {
										const Icon = productIdMapping[existingApp].Icon;

										return (
											<Box key={existingApp} xcss={styles.newApp}>
												<Icon
													appearance="brand"
													shouldUseHexLogo
													shouldUseNewLogoDesign
													size="xsmall"
												/>
												<Box xcss={styles.existingAppText}>
													<Heading size="xxsmall">{productIdMapping[existingApp].header}</Heading>
												</Box>
											</Box>
										);
									})}
								</Box>
							</React.Fragment>
						) : null}

						<Box xcss={styles.cta}>
							<Box>
								<Button appearance="primary" onClick={handleTryNowClick} testId="primary-cta-v2">
									<FormattedMessage {...confirmationCtaText} />
								</Button>
							</Box>
						</Box>
					</Box>
					<Box xcss={styles.rightColumn}>
						<Image
							src={isDarkMode ? VisualDark : VisualLight}
							srcDark={VisualDark}
							alt={formatMessage(confirmationMessages.visualAlt)}
							testId="teamwork-collection-visual"
							width="345"
							height="400"
						/>
					</Box>
				</Box>
				<Box xcss={styles.legalText}>
					{canActivate && (
						<LegalText
							hasPaymentDetails={hasPaymentDetails}
							edition={edition}
							ctaText={confirmationCtaText}
						/>
					)}
				</Box>
			</Box>
		</ScreenLayout>
	);
};
